import React, { useContext, useState } from 'react';
import { ReactSVG } from 'react-svg';
import { TmoButtons } from '@tmobile/tmo-bps-syncup-web-component-lib';

// Services and utilities
import iotFirmwareService from '../../../../services/iotFirmwareService';
import SyncUpProductContext from '../../../../context/SyncUpProductContext';
import StatusContext from '../../../../context/StatusContext';
import { trackCustomEvents } from '../../../../utils/appInsight_analytics';

// Styling and assets
import style from './ManageDevices.module.css';
import deleteIcon from '../../../../static/assets/icons/action/delete.svg';

/**
 * DeleteDevice Component
 * ======================
 * Provides device removal functionality from firmware groups with
 * user feedback, analytics tracking, and error handling.
 * @param {Object} props - Component properties
 * @param {Object} props.group - Firmware group information
 * @param {string} props.group.id - Unique group identifier
 * @param {string} props.group.name - Human-readable group name
 * @param {Function} props.onRemove - Callback to refresh parent data after removal
 * @param {string} props.device - Device identifier (IMEI, serial number, etc.)
 */
function DeleteDevice({ group, onRemove, device }) {
  const [deleting, setDeleting] = useState(false);
  const syncUpProductContext = useContext(SyncUpProductContext);
  const { addSuccessMessage, addErrorMessage } = useContext(StatusContext);

  const { name, id } = group;
  const handleRemoveDevice = async deviceId => {
    try {
      trackCustomEvents(`Remove Devices`, {
        groupId: id,
        hardwareId: deviceId
      });
      setDeleting(true);
      await iotFirmwareService.deleteDeviceFromFirmwareGroup({
        groupId: id,
        hardwareId: deviceId,
        product: syncUpProductContext.syncUpAppName
      });
      await onRemove();
      addSuccessMessage({
        message: `Successfully deleted device ${deviceId} from ${name}`
      });
      setTimeout(async () => {
        setDeleting(false);
      }, 3000);
    } catch (error) {
      setDeleting(false);
      addErrorMessage({ error });
    }
  };

  return (
    <div key={device} className={style.list_item}>
      {/* Device identification display */}
      <span className={style.list_item_name}>{device}</span>

      {/* Delete action button */}
      <TmoButtons
        className={deleting && style.btn_deleting} // Apply loading styles when deleting
        type="magenta_link" // T-Mobile standard link button styling
        icon={<ReactSVG src={deleteIcon} />} // Delete icon for clear action indication
        onClick={() => handleRemoveDevice(device)} // Trigger removal with device identifier
      />
    </div>
  );
}

export default DeleteDevice;
